import { z } from "zod";
import {
  EventIdPublicSchema,
  EventSlugSchema,
  UserIdSchema,
} from "./branded-id.schemas.js";
import { SupportedTranslationLocaleSchema } from "./locale.schemas.js";
import { LocalizedTextSchema } from "./localized-text.schemas.js";
import { CrmIntegrationViewUnionSchema } from "./crm-integration.schemas.js";
import { EventAllowedCommunicationChannelSchema } from "./event-allowed-communication-channel.schemas.js";
import { EventLocationSchema } from "./event-location.schemas.js";
import { EventStatusSchema } from "./event-state.schemas.js";
import { EventRoleSchema } from "../constants/event-role.constants.js";
import { dataWithRolesSchema } from "./data-with-roles.schemas.js";
import {
  EventBrandingUrlSchema,
  WritableEventBrandingUrlSchema,
} from "./event-branding-url.schemas.js";

const _eventCreatableSchema = z.object({
  name: LocalizedTextSchema,

  timezone: z.string(),

  startAt: z.coerce.date(),

  endAt: z.coerce.date(),

  languages: z
    .tuple([SupportedTranslationLocaleSchema])
    .rest(SupportedTranslationLocaleSchema)
    .readonly(),

  location: EventLocationSchema.optional(),

  description: z.string().optional(),

  logoUrl: WritableEventBrandingUrlSchema.optional(),

  // Branding the join page wears: a full-bleed key visual above the lockup,
  // and a sponsor credited beside the organizer.
  keyVisualUrl: WritableEventBrandingUrlSchema.optional(),

  sponsorLogoUrl: WritableEventBrandingUrlSchema.optional(),

  sponsorName: z.string().optional(),

  // What Porter says about the sponsor when it credits them in a greeting.
  sponsorFact: z.string().optional(),

  hostName: z.string().optional(),

  // The host's own words, delivered in the member's language when Porter onboards them.
  hostOnboardingMessage: LocalizedTextSchema.optional(),

  allowedCommunicationChannels: z
    .array(EventAllowedCommunicationChannelSchema)
    .readonly()
    .default([]),

  isolated: z.boolean().default(false),

  isDemo: z.boolean().default(false),

  slug: EventSlugSchema.nullable().optional(),
});

export const EventCreatableSchema = _eventCreatableSchema.readonly();
export type EventCreatable = z.infer<typeof EventCreatableSchema>;

const _eventSchema = _eventCreatableSchema.extend({
  logoUrl: EventBrandingUrlSchema.optional(),
  keyVisualUrl: EventBrandingUrlSchema.optional(),
  sponsorLogoUrl: EventBrandingUrlSchema.optional(),

  idPublic: EventIdPublicSchema,

  status: EventStatusSchema,

  createdBy: UserIdSchema.nullable(),

  slug: EventSlugSchema.nullable(),

  createdAt: z.coerce.date(),

  updatedAt: z.coerce.date(),

  crm: CrmIntegrationViewUnionSchema.optional(),

  porterAdditionalContext: z.string().optional(),
});

export const EventSchema = _eventSchema.readonly();
export type Event = z.infer<typeof EventSchema>;

const _eventPublicViewSchema = _eventSchema.pick({
  idPublic: true,
  slug: true,
  name: true,
  logoUrl: true,
  keyVisualUrl: true,
  sponsorLogoUrl: true,
  sponsorName: true,
  allowedCommunicationChannels: true,
  isolated: true,
  languages: true,
});
export const EventPublicViewSchema = dataWithRolesSchema(
  _eventPublicViewSchema.readonly(),
  EventRoleSchema,
);
export type EventPublicView = z.infer<typeof EventPublicViewSchema>;

const _eventUserViewSchema = _eventPublicViewSchema.extend(
  _eventSchema.pick({
    status: true,
    startAt: true,
    endAt: true,
    timezone: true,
    languages: true,
    location: true,
    description: true,
    hostName: true,
  }).shape,
);
// $roles stamped via dataWithRolesSchema so frontend can gate UI by membership.
export const EventUserViewSchema = dataWithRolesSchema(
  _eventUserViewSchema.readonly(),
  EventRoleSchema,
);
export type EventUserView = z.infer<typeof EventUserViewSchema>;

const _eventOwnerViewSchema = _eventUserViewSchema.extend(
  _eventSchema.pick({
    createdBy: true,
    createdAt: true,
    updatedAt: true,
    sponsorFact: true,
    hostOnboardingMessage: true,
  }).shape,
);
export const EventOwnerViewSchema = dataWithRolesSchema(
  _eventOwnerViewSchema.readonly(),
  EventRoleSchema,
);
export type EventOwnerView = z.infer<typeof EventOwnerViewSchema>;

export const EventAdminViewSchema = dataWithRolesSchema(
  _eventSchema.readonly(),
  EventRoleSchema,
);
export type EventAdminView = z.infer<typeof EventAdminViewSchema>;

export const EventUpdateableHostViewSchema = _eventCreatableSchema
  .omit({
    isolated: true,
    isDemo: true,
    slug: true,
    allowedCommunicationChannels: true,
  })
  .partial()
  .extend({
    description: z.string().nullable().optional(),
    logoUrl: EventBrandingUrlSchema.nullable().optional(),
    keyVisualUrl: EventBrandingUrlSchema.nullable().optional(),
    sponsorLogoUrl: EventBrandingUrlSchema.nullable().optional(),
    sponsorName: z.string().nullable().optional(),
    sponsorFact: z.string().nullable().optional(),
    hostName: z.string().nullable().optional(),
    hostOnboardingMessage: LocalizedTextSchema.nullable().optional(),
    location: EventLocationSchema.nullable().optional(),
  });

export type EventUpdateableHostView = z.infer<
  typeof EventUpdateableHostViewSchema
>;

export const EventUpdateableInternalSchema = z.object({
  slug: EventSlugSchema.nullable().optional(),
  allowedCommunicationChannels: z
    .array(EventAllowedCommunicationChannelSchema)
    .nullable()
    .optional(),
  isDemo: z.boolean().optional(),
  isolated: z.boolean().optional(),
  porterAdditionalContext: z.string().nullable().optional(),
});

export type EventUpdateableInternalView = z.infer<
  typeof EventUpdateableInternalSchema
>;
